import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, ClipboardCheck, AlertCircle, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import { clinicApi } from '../services/clinicApi';
import VerificationWizardModal from '../components/verification/VerificationWizardModal';
import './VerificationQueuePage.css';

export default function VerificationQueuePage() {
  const queryClient = useQueryClient();
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [includeInactive, setIncludeInactive] = useState(false);
  const pageSize = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['verification-queue', page, pageSize, search, sortColumn, sortDirection, includeInactive],
    queryFn: () => clinicApi.getVerificationQueue(page, pageSize, search || undefined, sortColumn || undefined, sortDirection || undefined, includeInactive || undefined),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleRowClick = (clinicId: number) => {
    setSelectedClinicId(clinicId);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getScoreTier = (score?: number): string => {
    if (score == null) return 'unknown';
    if (score >= 80) return 'high';
    if (score >= 50) return 'medium';
    return 'low';
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else {
        setSortColumn(null);
        setSortDirection('ASC');
      }
    } else {
      setSortColumn(column);
      setSortDirection('ASC');
    }
    setPage(1);
  };

  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'ASC' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  return (
    <div className="verification-queue-page">
      <div className="page-header">
        <div className="page-title-row">
          <ClipboardCheck size={28} />
          <h1>Verification Queue</h1>
        </div>
        <p className="page-subtitle">
          Clinics needing re-verification. Click a clinic to begin the verification process.
        </p>
      </div>

      <div className="queue-toolbar">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search by clinic name or code..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search clinics"
            />
            {searchInput && (
              <button type="button" className="clear-search" onClick={handleClearSearch}>
                &times;
              </button>
            )}
          </div>
          <button type="submit" className="btn-search">Search</button>
        </form>

        <label className="filter-toggle">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => { setIncludeInactive(e.target.checked); setPage(1); }}
          />
          Show inactive
        </label>

        {data && (
          <div className="queue-summary">
            <span className="total-count">{data.totalCount}</span> clinic{data.totalCount !== 1 ? 's' : ''} need verification
          </div>
        )}
      </div>

      {isLoading && (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading verification queue...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <AlertCircle size={24} />
          <p>Failed to load verification queue. Please try again.</p>
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="empty-state">
          <ClipboardCheck size={48} />
          <h3>{search ? 'No matching clinics found' : 'All clinics are up to date!'}</h3>
          <p>{search ? 'Try a different search term.' : 'No clinics currently need re-verification.'}</p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="queue-table-container">
            <table className="queue-table">
              <thead>
                <tr>
                  <th className="col-sortable" onClick={() => handleSort('Name')}>
                    Clinic Name {renderSortIcon('Name')}
                  </th>
                  <th className="col-sortable" onClick={() => handleSort('Code')}>
                    Code {renderSortIcon('Code')}
                  </th>
                  <th className="col-sortable" onClick={() => handleSort('City')}>
                    City {renderSortIcon('City')}
                  </th>
                  <th className="col-sortable" onClick={() => handleSort('State')}>
                    State {renderSortIcon('State')}
                  </th>
                  <th className="col-sortable" onClick={() => handleSort('Phone')}>
                    Phone {renderSortIcon('Phone')}
                  </th>
                  <th className="col-sortable" onClick={() => handleSort('LastVerifiedDate')}>
                    Last Verified {renderSortIcon('LastVerifiedDate')}
                  </th>
                  <th className="col-sortable" onClick={() => handleSort('ReliabilityScore')}>
                    Reliability {renderSortIcon('ReliabilityScore')}
                  </th>
                  <th className="col-sortable" onClick={() => handleSort('DaysOverdue')}>
                    Status {renderSortIcon('DaysOverdue')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => handleRowClick(item.id)}
                    className="clickable-row"
                  >
                    <td className="col-name">{item.name}</td>
                    <td className="col-code">{item.code || '-'}</td>
                    <td>{item.city || '-'}</td>
                    <td>{item.state || '-'}</td>
                    <td>{item.phone || '-'}</td>
                    <td>{formatDate(item.lastVerifiedDate)}</td>
                    <td>
                      <span className={`reliability-badge reliability-${getScoreTier(item.reliabilityScore)}`}>
                        {item.reliabilityScore != null ? item.reliabilityScore.toFixed(0) : '--'}
                      </span>
                    </td>
                    <td>
                      {item.neverVerified ? (
                        <span className="badge badge-never">Never Verified</span>
                      ) : (
                        <span className="badge badge-overdue">
                          {item.daysOverdue} day{item.daysOverdue !== 1 ? 's' : ''} overdue
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={!data.hasPreviousPage}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              <span className="pagination-info">
                Page {data.pageNumber} of {data.totalPages}
              </span>
              <button
                className="pagination-btn"
                disabled={!data.hasNextPage}
                onClick={() => setPage(page + 1)}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
      <VerificationWizardModal
        isOpen={selectedClinicId !== null}
        clinicId={selectedClinicId}
        onClose={() => setSelectedClinicId(null)}
        onComplete={() => {
          setSelectedClinicId(null);
          queryClient.invalidateQueries({ queryKey: ['verification-queue'] });
        }}
      />
    </div>
  );
}
